import { ReactNode, memo, useCallback, useMemo, useRef, useState } from 'react'
import {
  Box, IconButton, Tooltip, Typography, Checkbox, TextField,
  Dialog, DialogTitle, DialogContent, Divider,
  Menu, MenuItem,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import TuneIcon from '@mui/icons-material/Tune'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import EditIcon from '@mui/icons-material/Edit'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import { BiShapeTriangle } from 'react-icons/bi'
import { FS_SM, FS_XS, ICON_LG, ICON_MD, ICON_SM } from '../../common/theme'
import StatePill from '../../comps/StatePill'
import StatePicker from '../../comps/StatePicker'
import { FancyButton } from '../../comps/FancyButtons'
import TaWordLimit from '../../comps/TaWordLimit'
import TaWordCount from '../../comps/TaWordCount'
import GenerateResponseMiniDot from '../../comps/buttons/GenerateResponseMiniDot'
import GenerateTraceMiniDot from '../../comps/buttons/GenerateTraceMiniDot'
import GenerateScoringMiniDot from '../../comps/buttons/GenerateScoringMiniDot'
import TAChip from '../../comps/TAChip'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { questionAnchorId, type Project, type Question } from '../../types'
import { EMPTY_ANSWER } from '../../types/answerwebresult'
import { EMPTY_BIDEVAL } from '../../types/bidevalwebresult'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import { QUESTION_PAGE_SIZE } from '../../comps/BidQuestion'

// Reused across renders so the default-value shortcut in the
// destructure below doesn't hand a new Set/reference into every
// downstream memoisation each pass.
const EMPTY_SHA_SET: Set<string> = new Set()
const NOOP_SELECTION_CHANGE = (_next: Set<string>) => { /* noop */ }

interface BidQuestionsLeftPanelProps {
  allQuestions: Question[]   // every filtered question
  page: number               // 1-based current RHS page (used to switch pages on click)
  onPage: (p: number) => void
  onCollapse: () => void
  // Selection wiring is only meaningful when `bulkActions` is on. Left
  // optional so ResponseBuilderTab-style consumers can skip both.
  selectedShas?: Set<string>
  onSelectionChange?: (next: Set<string>) => void
  onQuestionChange?: (q: Question) => void
  onSelectQuestion?: (q: Question) => void
  // Open the batch-process modal (shared with the project-level "play"
  // button) seeded with the given questions.
  onBatchProcess?: (questions: Question[]) => void
  // When false, hides every bulk-action affordance: row / section /
  // toolbar checkboxes, the "X selected" chip, the Edit + Batch
  // buttons. The panel then behaves like a plain single-select
  // navigator (used by ResponseBuilderTab where bulk operations don't
  // make sense).
  bulkActions?: boolean
  // Optional controlled section-filter wiring. When
  // `onSectionFilterChange` is supplied the panel treats
  // `sectionFilter` as the source of truth (mirroring an external
  // section picker — e.g. BidQuestionsTab's section pills across the
  // top). Without a handler the panel manages the filter itself.
  //   `undefined` = show every section
  //   ''          = show only unsectioned questions
  //   any string  = show only that named section
  sectionFilter?: string | undefined
  onSectionFilterChange?: (s: string | undefined) => void
}

type HoveredAction = 'answer' | 'trace' | 'score' | null

const ACTION_LABELS: Record<Exclude<HoveredAction, null>, { icon: ReactNode; label: string }> = {
  answer: { icon: <AutorenewIcon sx={{ fontSize: ICON_SM }} />, label: 'Generate Answer' },
  trace: { icon: <BiShapeTriangle size={12} />, label: 'Generate Trace' },
  score: { icon: <AssessmentOutlinedIcon sx={{ fontSize: ICON_LG }} />, label: 'Generate Estimated Score' },
}

// Plain-text version of the question html for the row snippet line.
// Same helper BidQuestionListPicker uses so the two panels stay
// visually consistent when "question preview" is toggled on.
function questionSnippet(html?: string | null): string {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent ?? '').replace(/\s+/g, ' ').trim()
}

// Tooltip body - same shape as the original QuestionPaginator pill tooltip.
// When the cursor is hovering one of the row's three action icons, an extra
// row appears at the bottom describing that action.
function QuestionTooltipBody({ question, hoveredAction }: {
  question: Question
  hoveredAction?: HoveredAction
}) {
  const sim = (question as any).answerobj?.simint ?? null
  const score = (question as any).bideval?.scoreint ?? null
  const action = hoveredAction ? ACTION_LABELS[hoveredAction] : null
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, maxWidth: 360 }}>
      {(question as any).numbering && (
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{(question as any).numbering}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <StatePill
          state={(question as any).state ?? 'todo'}
        />
      </Box>
      {action && (
        <Box sx={{
          mt: 0.5, py: 0.5, borderTop: '1px solid rgba(255,255,255,0.18)',
          borderBottom: '1px solid rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', gap: 0.5
        }}>
          {action.icon}
          <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{action.label}</Typography>
        </Box>
      )}
      <TaWordCount
        structure={question.answerobj?.answerstructure}
        question={question}
        replylimit={(question as any).replylimit}
      />
      {sim != null && (
        <Typography variant="caption" sx={{ opacity: 0.85 }}>Trace similarity: {sim}%</Typography>
      )}
      {score != null && (
        <Typography variant="caption" sx={{ opacity: 0.85 }}>Score: {score}%</Typography>
      )}
      {(question as any).question && (
        <Box sx={{ mt: 0.25, opacity: 0.75, fontSize: '0.7rem' }}>
          <span dangerouslySetInnerHTML={{ __html: (question as any).question }} />
        </Box>
      )}

    </Box>
  )
}

export default function BidQuestionsLeftPanel({
  allQuestions, page, onPage, onCollapse,
  selectedShas = EMPTY_SHA_SET,
  onSelectionChange = NOOP_SELECTION_CHANGE,
  onQuestionChange, onSelectQuestion,
  onBatchProcess,
  bulkActions = true,
  sectionFilter: controlledSectionFilter,
  onSectionFilterChange,
}: BidQuestionsLeftPanelProps) {
  console.log('BidQuestionsLeftPanel render');
  const { updateQuestion } = useAppActions()
  // `project` is kept on the props for API symmetry with the other
  // bid-manager surfaces but the per-row action buttons read their own
  // `currentProject` from useApp internally, so we don't need it here.
  const [scrolledSha, setScrolledSha] = useState<string | null>(null)
  const [editingNumberingSha, setEditingNumberingSha] = useState<string | null>(null)
  const [editingNumbering, setEditingNumbering] = useState('')
  // Display-options toggles — driven by the three-dots menu next to
  // the collapse chevron. `showActions` gates the trio of mini-dot
  // buttons on each row; `showPreview` toggles a one-line snippet of
  // the question text below the numbering (same shape as
  // BidQuestionListPicker's row).
  const [showActions, setShowActions] = useState(true)
  const [showPreview, setShowPreview] = useState(false)
  // Anchor for the display-options menu. Toggling either checkbox
  // closes it — same behaviour as most MUI menus so the user isn't
  // stuck with a floating popover after committing a choice.
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null)
  const closeOptions = () => setOptionsAnchor(null)

  // Section-filter internal state. When the parent supplies
  // `onSectionFilterChange` the controlled value wins and the writer
  // routes through the callback instead of `setInternalSectionFilter`.
  //   `undefined` = show every section
  //   ''          = only unsectioned questions
  //   any string  = only that named section
  const [internalSectionFilter, setInternalSectionFilter] = useState<string | undefined>(undefined)
  const sectionFilter = onSectionFilterChange ? controlledSectionFilter : internalSectionFilter
  const setSectionFilter = (s: string | undefined) => {
    if (onSectionFilterChange) onSectionFilterChange(s)
    else setInternalSectionFilter(s)
  }
  const [sectionAnchor, setSectionAnchor] = useState<HTMLElement | null>(null)
  const closeSectionMenu = () => setSectionAnchor(null)
  // Discover every section present in the incoming question set — used
  // by the section menu and to hide the chip when nothing is grouped.
  const sectionInfo = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    let hasUnsectioned = false
    for (const q of allQuestions) {
      const s = ((q as any).section ?? '') as string
      if (s) {
        if (!seen.has(s)) { seen.add(s); list.push(s) }
      } else {
        hasUnsectioned = true
      }
    }
    return { list, hasUnsectioned }
  }, [allQuestions])

  // Apply the section filter before anything downstream (items, count,
  // select-all) sees the questions. `undefined` short-circuits to the
  // full input.
  const sectionScopedQuestions = useMemo(() => {
    if (sectionFilter === undefined) return allQuestions
    return allQuestions.filter(q => ((q as any).section ?? '') === sectionFilter)
  }, [allQuestions, sectionFilter])

  // Bulk runs (Generate / Trace / Score) are now driven from the project-
  // level batch process modal, which is opened via `onBatchProcess` from the
  // bulk-edit modal. Restricted to the currently-visible (section-scoped)
  // subset so a section filter doesn't quietly pull in hidden rows.
  const selectedQuestions = sectionScopedQuestions.filter(q => selectedShas.has(q.questionsha))

  // Latest-ref bundle: row callbacks below read state via this ref so they
  // can stay identity-stable across every parent re-render. Without that,
  // `AppContext.updateQuestion` (which rebuilds `currentProject` on every
  // patch) would invalidate every row's callback prop and defeat the
  // `QuestionRow` `React.memo` skip.
  const latestRef = useRef({
    allQuestions, page, onPage, selectedShas, onSelectionChange,
    onQuestionChange, onSelectQuestion,
    editingNumberingSha, editingNumbering,
  })
  latestRef.current = {
    allQuestions, page, onPage, selectedShas, onSelectionChange,
    onQuestionChange, onSelectQuestion,
    editingNumberingSha, editingNumbering,
  }

  const handleStartNumberingEdit = useCallback((sha: string, current: string | undefined) => {
    setEditingNumberingSha(sha)
    setEditingNumbering(current ?? '')
  }, [])

  const handleCommitNumbering = useCallback(() => {
    const { editingNumberingSha, editingNumbering, allQuestions, onQuestionChange } = latestRef.current
    if (!editingNumberingSha) return
    const sha = editingNumberingSha
    const value = editingNumbering.trim()
    setEditingNumberingSha(null)
    if (!onQuestionChange) return
    const q = allQuestions.find(qq => qq.questionsha === sha)
    if (q && (q.numbering ?? '') !== value) {
      onQuestionChange({ ...q, numbering: value || undefined } as Question)
    }
  }, [])

  const handleCancelNumbering = useCallback(() => {
    setEditingNumberingSha(null)
  }, [])

  const handleNumberingChange = useCallback((value: string) => {
    setEditingNumbering(value)
  }, [])

  const handleToggleSelect = useCallback((sha: string) => {
    const { selectedShas, onSelectionChange } = latestRef.current
    const next = new Set(selectedShas)
    if (next.has(sha)) next.delete(sha); else next.add(sha)
    onSelectionChange(next)
  }, [])

  // Insert a section header before the first question in each section run.
  // Ordinals are 1-based across the full filtered list.
  type Item =
    | { type: 'section'; name: string; shas: string[] }
    | { type: 'q'; q: Question; ordinal: number; indexInAll: number; pageOfQ: number }
  const items = useMemo<Item[]>(() => {
    const out: Item[] = []
    let lastSection: string | null = '__init__'
    let currentSectionIdx = -1
    sectionScopedQuestions.forEach((q, i) => {
      const sec = ((q as any).section ?? null) as string | null
      if (sec !== lastSection) {
        if (sec) {
          out.push({ type: 'section', name: sec, shas: [] })
          currentSectionIdx = out.length - 1
        } else {
          currentSectionIdx = -1
        }
        lastSection = sec
      }
      if (currentSectionIdx >= 0) {
        const sectionItem = out[currentSectionIdx]
        if (sectionItem.type === 'section') sectionItem.shas.push(q.questionsha)
      }
      out.push({
        type: 'q',
        q,
        ordinal: i + 1,
        indexInAll: i,
        pageOfQ: Math.floor(i / QUESTION_PAGE_SIZE) + 1,
      })
    })
    return out
  }, [sectionScopedQuestions])

  const handleClickQuestion = useCallback((q: Question, indexInAll: number) => {
    const { page, onPage, onSelectQuestion } = latestRef.current
    setScrolledSha(q.questionsha)
    if (onSelectQuestion) onSelectQuestion(q)
    const targetPage = Math.floor(indexInAll / QUESTION_PAGE_SIZE) + 1
    const scrollNow = () => {
      document.getElementById(questionAnchorId(q.questionsha))?.scrollIntoView({ behavior: 'auto', block: 'start' })
    }
    if (targetPage !== page) {
      onPage(targetPage)
      // Wait for the new page's BidQuestion components to mount before scrolling.
      setTimeout(scrollNow, 80)
    } else {
      scrollNow()
    }
  }, [])

  const toggleSectionSelection = (sectionShas: string[]) => {
    const next = new Set(selectedShas)
    const allInSection = sectionShas.every(s => next.has(s))
    if (allInSection) sectionShas.forEach(s => next.delete(s))
    else sectionShas.forEach(s => next.add(s))
    onSelectionChange(next)
  }

  // Bulk state change for selected questions.
  const handleBulkState = (state: string) => {
    if (selectedShas.size === 0 || !onQuestionChange) return
    const map = new Map(allQuestions.map(q => [q.questionsha, q]))
    selectedShas.forEach(sha => {
      const q = map.get(sha)
      if (q) onQuestionChange({ ...q, state } as Question)
    })
  }

  const handleBulkWordCount = (replylimit: any) => {
    if (selectedShas.size === 0 || !onQuestionChange) return
    const map = new Map(allQuestions.map(q => [q.questionsha, q]))
    selectedShas.forEach(sha => {
      const q = map.get(sha)
      if (q) onQuestionChange({ ...q, replylimit } as Question)
    })
  }

  // Clear answer / trace / score / sources / tweaks for every selected
  // question (the question text itself stays intact). Goes through
  // AppContext.updateQuestion so the patch merges in + flips
  // `modified: true` for each row.
  const handleBulkClear = () => {
    if (selectedShas.size === 0) return
    selectedShas.forEach(sha => {
      updateQuestion(sha, {
        answerobj: EMPTY_ANSWER,
        bideval: EMPTY_BIDEVAL,
        structure: '',
        sourcedocs: [],
        structuretweaks: [],
      })
    })
    setBulkModalOpen(false)
    addToast('success', `Cleared ${selectedShas.size} question${selectedShas.size !== 1 ? 's' : ''}`)
  }

  const handleStartBatchProcess = () => {
    if (selectedShas.size === 0) return
    if (onBatchProcess) {
      onBatchProcess(selectedQuestions)
    }
  }

  const [bulkModalOpen, setBulkModalOpen] = useState(false)

  const selectedCount = selectedShas.size
  const allSelected = sectionScopedQuestions.length > 0
    && selectedCount === sectionScopedQuestions.length
  const toggleSelectAll = () => {
    if (allSelected) onSelectionChange(new Set())
    else onSelectionChange(new Set(sectionScopedQuestions.map(q => q.questionsha)))
  }

  return (
    <Box data-testid="bq_leftpanel" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Toolbar - select-all + bulk state (when selection) + collapse */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 0.5, py: 0.5,
        borderBottom: 1, borderColor: 'divider',
        flexShrink: 0,
        minHeight: 32,
      }}>
        {bulkActions && (
          <Tooltip title={allSelected ? 'Deselect all' : 'Select all questions'} disableInteractive>
            <Checkbox
              size="small"
              data-testid="bq_leftpanel_selectall"
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={toggleSelectAll}
              sx={{ p: 0.25 }}
            />
          </Tooltip>
        )}
        {bulkActions && selectedCount > 0 ? (
          <TAChip
            kind="selected"
            label={`${selectedCount} selected`}
            onDelete={() => onSelectionChange(new Set())}
          />
        ) : (
          (() => {
            // The count text itself is the section-filter trigger
            // whenever `allQuestions` carries any sections. Keeping
            // this inline (rather than a separate Chip) so the
            // toolbar stays compact.
            const hasSections = sectionInfo.list.length > 0 || sectionInfo.hasUnsectioned
            const sectionLabel = sectionFilter === undefined
              ? null
              : (sectionFilter === '' ? 'Unsectioned' : sectionFilter)
            return (
              <Box
                data-testid="bq_leftpanel_count"
                onClick={hasSections
                  ? (e: React.MouseEvent<HTMLElement>) => setSectionAnchor(e.currentTarget)
                  : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.25,
                  ...(hasSections ? {
                    cursor: 'pointer',
                    borderRadius: 0.5,
                    px: 0.5, mx: -0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  } : null),
                }}
              >
                <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.6, whiteSpace: 'nowrap' }}>
                  {sectionScopedQuestions.length} question{sectionScopedQuestions.length !== 1 ? 's' : ''}
                  {sectionLabel && (
                    <Box component="span" sx={{ opacity: 0.85, fontWeight: 600, ml: 0.5 }}>
                      · {sectionLabel}
                    </Box>
                  )}
                </Typography>
                {hasSections && (
                  <ArrowDropDownIcon sx={{ fontSize: ICON_SM, opacity: 0.55 }} />
                )}
              </Box>
            )
          })()
        )}
        {(sectionInfo.list.length > 0 || sectionInfo.hasUnsectioned) && (
          <Menu
            anchorEl={sectionAnchor}
            open={!!sectionAnchor}
            onClose={closeSectionMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem
              data-testid="bq_leftpanel_section_filter_all"
              onClick={() => { setSectionFilter(undefined); closeSectionMenu() }}
              sx={{ py: 0.5, fontSize: FS_SM }}
            >
              All sections
            </MenuItem>
            {sectionInfo.list.map(s => (
              <MenuItem
                key={s}
                onClick={() => { setSectionFilter(s); closeSectionMenu() }}
                sx={{ py: 0.5, fontSize: FS_SM }}
              >
                {s}
              </MenuItem>
            ))}
            {sectionInfo.hasUnsectioned && (
              <MenuItem
                data-testid="bq_leftpanel_section_filter_unsectioned"
                onClick={() => { setSectionFilter(''); closeSectionMenu() }}
                sx={{ py: 0.5, fontSize: FS_SM }}
              >
                Unsectioned
              </MenuItem>
            )}
          </Menu>
        )}
        <Box sx={{ flex: 1 }} />
        {bulkActions && selectedCount > 0 && (
          <>
            <Tooltip title="Edit selected questions" disableInteractive>
              <IconButton
                size="small"
                data-testid="bq_leftpanel_bulk_edit"
                onClick={() => setBulkModalOpen(true)}
                sx={{ p: 0.25 }}
              >
                <EditIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Batch process selected questions" disableInteractive>
              <span>
                <IconButton
                  size="small"
                  data-testid="bq_leftpanel_bulk_batch"
                  onClick={handleStartBatchProcess}
                  disabled={!onBatchProcess}
                  sx={{ p: 0.25, color: 'primary.main' }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="Display options" placement="left" disableInteractive>
          <IconButton
            data-testid="bq_leftpanel_options_btn"
            size="small"
            onClick={(e) => setOptionsAnchor(e.currentTarget)}
            sx={{ p: 0.25 }}
          >
            <TuneIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Hide panel" placement="left" disableInteractive>
          <IconButton data-testid="bq_leftpanel_collapse_btn" size="small" onClick={onCollapse} sx={{ p: 0.25 }}>
            <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={optionsAnchor}
          open={!!optionsAnchor}
          onClose={() => setOptionsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            data-testid="bq_leftpanel_options_actions"
            onClick={() => { setShowActions(v => !v); closeOptions() }}
            sx={{ py: 0.5, gap: 1, fontSize: FS_SM }}
          >
            <Checkbox size="small" checked={showActions} tabIndex={-1} sx={{ p: 0.25 }} />
            Actions
          </MenuItem>
          <MenuItem
            data-testid="bq_leftpanel_options_preview"
            onClick={() => { setShowPreview(v => !v); closeOptions() }}
            sx={{ py: 0.5, gap: 1, fontSize: FS_SM }}
          >
            <Checkbox size="small" checked={showPreview} tabIndex={-1} sx={{ p: 0.25 }} />
            Question preview
          </MenuItem>
        </Menu>
      </Box>

      {/* Question list */}
      <Box data-testid="bq_leftpanel_list" sx={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>No questions</Typography>
          </Box>
        ) : items.map((item, idx) => {
          if (item.type === 'section') {
            const allInSection = item.shas.length > 0 && item.shas.every(s => selectedShas.has(s))
            const anyInSection = item.shas.some(s => selectedShas.has(s))
            return (
              <Box
                key={`sec-${idx}`}
                data-testid="bq_leftpanel_section"
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 0.5, py: 0.5,
                  bgcolor: 'action.selected',
                  borderBottom: 1, borderColor: 'divider',
                }}
              >
                {bulkActions && item.shas.length > 0 && (
                  <Checkbox
                    size="small"
                    data-testid="bq_leftpanel_section_checkbox"
                    checked={allInSection}
                    indeterminate={!allInSection && anyInSection}
                    onClick={(e) => { e.stopPropagation(); toggleSectionSelection(item.shas) }}
                    sx={{ p: 0.25 }}
                  />
                )}
                <FolderOutlinedIcon sx={{ fontSize: ICON_SM, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="caption" sx={{
                  fontWeight: 600, fontSize: FS_SM,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {item.name}
                </Typography>
              </Box>
            )
          }
          const sha = item.q.questionsha
          const isSelected = selectedShas.has(sha)
          const isScrolled = scrolledSha === sha
          const isOnCurrentPage = item.pageOfQ === page
          const indented = !!(item.q as any).section
          const isEditing = editingNumberingSha === sha
          return (
            <QuestionRow
              key={sha}
              q={item.q}
              ordinal={item.ordinal}
              indexInAll={item.indexInAll}
              isSelected={isSelected}
              isScrolled={isScrolled}
              isOnCurrentPage={isOnCurrentPage}
              indented={indented}
              isEditingNumbering={isEditing}
              editingNumberingValue={isEditing ? editingNumbering : ''}
              showActions={showActions}
              showPreview={showPreview}
              showCheckbox={bulkActions}
              onClickQuestion={handleClickQuestion}
              onToggleSelect={handleToggleSelect}
              onStartNumberingEdit={handleStartNumberingEdit}
              onCommitNumbering={handleCommitNumbering}
              onCancelNumbering={handleCancelNumbering}
              onNumberingChange={handleNumberingChange}
            />
          )
        })}
      </Box>

      <BulkEditQuestionsModal
        open={bulkModalOpen}
        selectedCount={selectedCount}
        onState={handleBulkState}
        onWordCount={handleBulkWordCount}
        onClear={handleBulkClear}
        onClose={() => setBulkModalOpen(false)}
      />
    </Box>
  )
}

// One question row, memoised so updates that don't touch this question's
// reference skip render entirely. `AppContext.updateQuestion` rebuilds
// `currentProject` on every patch but preserves the identity of every
// untouched question - combined with the parent's identity-stable
// callbacks (via the latest-ref pattern above), this means typing into
// the answer of question X only re-renders X's row, not all 10 rows in
// the visible page.
interface QuestionRowProps {
  q: Question
  ordinal: number
  indexInAll: number
  isSelected: boolean
  isScrolled: boolean
  isOnCurrentPage: boolean
  indented: boolean
  isEditingNumbering: boolean
  editingNumberingValue: string
  showActions: boolean
  showPreview: boolean
  showCheckbox: boolean
  onClickQuestion: (q: Question, indexInAll: number) => void
  onToggleSelect: (sha: string) => void
  onStartNumberingEdit: (sha: string, current: string | undefined) => void
  onCommitNumbering: () => void
  onCancelNumbering: () => void
  onNumberingChange: (value: string) => void
}

const QuestionRow = memo(function QuestionRow({
  q, ordinal, indexInAll, isSelected, isScrolled, isOnCurrentPage,
  indented, isEditingNumbering, editingNumberingValue,
  showActions, showPreview, showCheckbox,
  onClickQuestion, onToggleSelect, onStartNumberingEdit, onCommitNumbering,
  onCancelNumbering, onNumberingChange,
}: QuestionRowProps) {
  const sha = q.questionsha
  const numbering = (q as any).numbering || String(ordinal)
  // Hover state is local - moving the cursor across mini-dot buttons no
  // longer flips state in the parent (which would re-render every row).
  // Only the hovered row re-renders to swap its tooltip body.
  const [hoveredAction, setHoveredAction] = useState<HoveredAction>(null)
  const clearHover = useCallback(() => setHoveredAction(null), [])
  const snippet = showPreview ? questionSnippet((q as any).question) : ''
  return (
    <Tooltip title={<QuestionTooltipBody question={q} hoveredAction={hoveredAction} />} placement="right" enterDelay={400} disableInteractive>
      <Box
        data-testid="bq_leftpanel_row"
        data-sha={sha}
        data-selected={isSelected ? 'true' : 'false'}
        data-on-page={isOnCurrentPage ? 'true' : 'false'}
        onClick={() => onClickQuestion(q, indexInAll)}
        sx={{
          display: 'flex', flexDirection: 'column', gap: 0.25,
          pr: 0.5, py: 0.5,
          pl: indented ? 1.5 : 0.5,
          borderBottom: 1, borderColor: 'divider',
          bgcolor: isSelected ? 'action.selected' : isScrolled ? 'action.hover' : 'transparent',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {showCheckbox && (
            <Checkbox
              size="small"
              data-testid="bq_leftpanel_checkbox"
              checked={isSelected}
              onClick={(e) => { e.stopPropagation(); onToggleSelect(sha) }}
              sx={{ p: 0.25 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            {isEditingNumbering ? (
              <TextField
                size="small"
                autoFocus
                value={editingNumberingValue}
                placeholder={String(ordinal)}
                onChange={e => onNumberingChange(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  e.stopPropagation()
                  if (e.key === 'Enter') { e.preventDefault(); onCommitNumbering() }
                  if (e.key === 'Escape') { e.preventDefault(); onCancelNumbering() }
                }}
                onBlur={onCommitNumbering}
                inputProps={{ 'data-testid': 'bq_leftpanel_numbering_input' }}
                sx={{ width: '100%', maxWidth: 120, '& .MuiInputBase-input': { py: 0.15, px: 0.5, fontSize: FS_SM, fontWeight: 700 } }}
              />
            ) : (
              <Typography
                onClick={e => { e.stopPropagation(); onStartNumberingEdit(sha, (q as any).numbering) }}
                sx={{
                  fontSize: FS_SM, fontWeight: 700, lineHeight: 1,
                  color: 'text.primary',
                  cursor: 'pointer', userSelect: 'none',
                  minWidth: 0, maxWidth: '100%',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {numbering}
              </Typography>
            )}
          </Box>
          <StatePill state={(q as any).state ?? 'todo'} />
          {showActions && (
            <>
              <GenerateResponseMiniDot
                question={q}
                onMouseEnter={() => setHoveredAction('answer')}
                onMouseLeave={clearHover}
                testId="bq_leftpanel_answer_btn"
              />
              <GenerateTraceMiniDot
                question={q}
                onMouseEnter={() => setHoveredAction('trace')}
                onMouseLeave={clearHover}
                testId="bq_leftpanel_trace_btn"
                disableIfPresent
              />
              <GenerateScoringMiniDot
                question={q}
                onMouseEnter={() => setHoveredAction('score')}
                onMouseLeave={clearHover}
                testId="bq_leftpanel_score_btn"
                disableIfPresent
              />
            </>
          )}
        </Box>
        {showPreview && snippet && (
          <Typography
            variant="caption"
            sx={{
              fontSize: FS_XS, opacity: 0.7, lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              whiteSpace: 'normal',
              // Align under the numbering — indent past the checkbox
              // only when it's actually there.
              pl: showCheckbox ? 3.5 : 0.5,
            }}
          >
            {snippet}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
})

// Bulk-edit modal - triggered from the LHS toolbar's Edit button when one or
// more questions are selected. Surfaces the bulk edit fields (state, word
// count) and the destructive "clear" action. The batch-process action lives
// alongside the Edit button in the toolbar, not in this modal.
function BulkEditQuestionsModal({
  open, selectedCount,
  onState, onWordCount, onClear, onClose,
}: {
  open: boolean
  selectedCount: number
  onState: (s: string) => void
  onWordCount: (rl: any) => void
  onClear: () => void
  onClose: () => void
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          Edit {selectedCount} question{selectedCount !== 1 ? 's' : ''}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>

        {/* Set state */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="caption" sx={{ minWidth: 88, fontWeight: 500, opacity: 0.75 }}>State</Typography>
          <StatePicker state="todo" onChange={(s) => onState(s)} />
        </Box>

        {/* Set word count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="caption" sx={{ minWidth: 88, fontWeight: 500, opacity: 0.75 }}>Word count</Typography>
          <TaWordLimit replylimit={undefined} onChange={onWordCount} />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Destructive */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <FancyButton
            color="#d32f2f"
            testId="bq_bulk_modal_clear"
            onClick={onClear}
          >
            <ClearAllIcon sx={{ fontSize: ICON_SM }} />
            Clear workflow
          </FancyButton>
          <FancyButton onClick={onClose}>Close</FancyButton>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
